import React, { useCallback, useState } from "react"
import { convertNumber, ConvertNumberType } from "~/ui-rtk/utils/number-utils"

type TCaptionPreviewNumberProps = {
  value: number
  columnType: ConvertNumberType
  top?: boolean
}

// For percent columns, the shared convertNumber(..., full=true) path returns
// `${value}%`, which treats a ratio like 0.0651 as if it were already a
// percentage ("0.0651%") and leaks JS float noise ("0.06509999999999999%").
// Render percent tooltips as the real percentage with higher precision,
// matching what the cell shows.
const renderFullValue = (
  columnType: ConvertNumberType,
  value: number,
): string => {
  if (columnType === "percent" && Number.isFinite(value)) {
    // Use toPrecision to show full significant digits without float noise
    // or trailing-zero padding. 12 sig figs is enough for typical metrics
    // and strips artifacts like 0.06509999999999999.
    const pct = Number((value * 100).toPrecision(12))
    return `${pct}%`
  }
  return String(convertNumber(columnType, value, "--", true))
}

export const CaptionPreviewNumber: React.FC<TCaptionPreviewNumberProps> = ({
  value,
  columnType,
  top = false,
}) => {
  const [isFullCaption, setIsFullCaption] = useState(false)

  const handleMouseEnter = () => {
    setIsFullCaption(true)
  }

  const handleMouseLeave = () => {
    setIsFullCaption(false)
  }

  const render = useCallback(
    () => (
      <div
        className="relative w-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isFullCaption ? (
          <div
            className={`absolute flex flex-col bg-basic-blue rounded-lg z-50 ${top ? "bottom-10" : "-bottom-15"}  py-3 px-4 gap-2`}
          >
            <p className="max-h-75 overflow-y-auto scrollbar">
              {renderFullValue(columnType, value)}
            </p>
          </div>
        ) : null}
        <p className="w-full overflow-x-hidden text-ellipsis">
          {convertNumber(columnType, value, "--")}
        </p>
      </div>
    ),
    [value, isFullCaption],
  )

  return render()
}
